import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { systemUsers } from '../data';
import { initials } from '../utils/format';
interface UserLookupProps {
  value: string;
  label: string;
  onSelect: (name: string) => void;
  onCancel: () => void;
}

/** Searchable lookup against the system's users. */
export function UserLookup({
  value,
  label,
  onSelect,
  onCancel
}: UserLookupProps) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return systemUsers;
    return systemUsers.filter(u => [u.name, u.role, u.team, u.email].some(v => v.toLowerCase().includes(q)));
  }, [query]);
  useEffect(() => {
    setHighlight(0);
  }, [query]);
  useEffect(() => {
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({
      block: 'nearest'
    });
  }, [highlight]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const user = results[highlight];
      if (user) onSelect(user.name);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };
  return <div className="rounded-lg border border-blue-300 bg-white shadow-lg ring-2 ring-blue-100" role="combobox" aria-expanded="true" aria-haspopup="listbox" aria-label={`Search users for ${label}`} data-id="element-332" data-mp="1pr1mqclfbtxi">
      <div className="flex items-center gap-2 border-b border-slate-100 px-2.5 py-1.5" data-id="element-333" data-mp="2hg4vo5o99sgh">
        <SearchIcon className="h-4 w-4 shrink-0 text-slate-400" data-id="element-334" data-mp="flgu7oz9ni8i" />
        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Search users by name, role, or team…" aria-label="Search users" aria-autocomplete="list" className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400" data-id="element-335" data-mp="kjs3nylnfqq7" />
        <button type="button" onClick={onCancel} aria-label="Cancel" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100" data-id="element-336" data-mp="1t0yzaclwoi5">
          <XIcon className="h-4 w-4" data-id="element-337" data-mp="3o07kly43mvap" />
        </button>
      </div>

      <ul ref={listRef} role="listbox" aria-label="Users" className="max-h-56 overflow-y-auto py-1" data-id="element-338" data-mp="1haknn7bpe2x9">
        {value && <li data-id="element-339" data-mp="1m7t5t7wuq6yi">
            <button type="button" onClick={() => onSelect('')} className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-red-600" data-id="element-340" data-mp="1m6hzp1fxon48">
              <XIcon className="h-3.5 w-3.5" data-id="element-341" data-mp="3gi0zs65g0mye" />
              Clear assignment
            </button>
          </li>}

        {results.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400" data-id="element-342" data-mp="3pglbgncl5wd4">
            No users match “{query}”.
          </li>}

        {results.map((user, index) => {
        const selected = user.name === value;
        return <li key={user.id} data-id="element-343" data-mp="394yo8obbcda" data-mpi={index}>
              <button type="button" role="option" aria-selected={selected} onMouseEnter={() => setHighlight(index)} onClick={() => onSelect(user.name)} className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${index === highlight ? 'bg-blue-50' : ''}`} data-id="element-344" data-mp="3ug7pgz08ngmz">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${selected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`} data-id="element-345" data-mp="26hnec0j04um7">
                  {initials(user.name)}
                </span>
                <span className="min-w-0 flex-1" data-id="element-346" data-mp="1iup3bidnievo">
                  <span className="block truncate text-sm font-medium text-slate-900" data-id="element-347" data-mp="1nrfhg05wb0eg">
                    {user.name}
                  </span>
                  <span className="block truncate text-[11px] text-slate-500" data-id="element-348" data-mp="1spgxsdbxslm8">
                    {user.role} · {user.team}
                  </span>
                </span>
                {selected && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-blue-600" data-id="element-349" data-mp="1024f4e8wcxy7">
                    Current
                  </span>}
              </button>
            </li>;
      })}
      </ul>
    </div>;
}